import { Cents } from "@/components/ui"
import { formatMoney, formatMoneyRounded, type Money } from "@/lib/money"
import { tv, type VariantProps } from "@/lib/utils/tv"

export const priceVariants = tv({
  slots: {
    root: "flex max-w-full flex-wrap items-baseline gap-150 font-display",
    amount: "text-fg-strong tabular-nums tracking-tight",
    was: "text-fg-muted leading-none font-medium strike tabular-nums",
  },
  variants: {
    size: {
      sm: { amount: "text-body-sm font-extrabold", was: "text-caption-lg" },
      md: { amount: "text-price", was: "text-body-sm" },
      // The product-card / carousel price: a fixed 22px figure (paired with
      // weight="semibold" on the card).
      product: {
        root: "gap-100",
        amount: "font-text text-price-sm text-fg tracking-snug @card:text-price-md",
        was: "font-text text-fg-strike text-caption-lg leading-detail font-normal @card:text-body-sm",
      },
      lg: { amount: "text-price-lg", was: "text-body-lg" },
      card: { amount: "text-price-xl", was: "text-body-lg" },
      xl: { amount: "text-price-xl font-extrabold", was: "text-body-lg" },
    },
    // Price color reads the merchandising state, not the brand. Ink at full
    // price so blue stays reserved for actions; sale red only when a markdown
    // is actually present (the card sets it).
    tone: {
      default: {},
      sale: { amount: "text-fg-sale" },
      ink: { amount: "text-fg-heading" },
      inverse: { amount: "text-fg-inverted", was: "text-fg-inverted-muted" },
    },
    // Price sizes carry an 800 default weight; `semibold` steps it back to 600 for
    // surfaces (the product card) whose design wants a lighter figure.
    weight: {
      default: {},
      semibold: { amount: "font-semibold" },
    },
  },
  defaultVariants: { size: "md", tone: "default", weight: "default" },
})

interface PriceProps extends VariantProps<typeof priceVariants> {
  amount: Money
  was?: Money
  exact?: boolean
  className?: string
}

// Sale price always reads first; the crossed-out was-price follows it.
export const Price = ({ amount, was, size, tone, weight, exact = false, className }: PriceProps) => {
  const format = exact ? formatMoney : formatMoneyRounded
  const styles = priceVariants({ size, tone, weight })
  return (
    <span className={styles.root({ className })}>
      <span className={styles.amount()}>
        <Cents>{format(amount)}</Cents>
      </span>
      {was && (
        <span className={styles.was()}>
          <Cents>{format(was)}</Cents>
        </span>
      )}
    </span>
  )
}
